import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import type {
  AcceptAnswerDto,
  AnswerUpdateResponseDto,
  CreateAnswerResponseDto,
  DeleteAnswerResponseDto,
  QuestionAnswersDto,
  VoteResponseDto,
  VoteValues,
} from '@plugins/faq-lib/types';
import { Subscription } from '@plugins/shared/types';
import {
  CreateComment,
  UpdateComment,
} from '@plugins/shared/types/post/response';
import { SortType } from '@plugins/shared/types/sort';

import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AnswerService {
  private readonly http = inject(HttpClient);

  load(
    questionId: string,
    sort: SortType,
    skip = 0,
    take: number = environment.answerPageSize,
  ) {
    return this.http.get<QuestionAnswersDto>(
      `/api/question/single/${questionId}/answers`,
      {
        params: {
          skip: skip.toString(),
          take: take.toString(),
          sort,
        },
      },
    );
  }

  getAnswerPagination(questionId: string, answerId: string, sort: SortType) {
    return this.http.get<{ skip: number; take: number }>(
      `/api/question/single/${questionId}/answers/${answerId}/pagination`,
      {
        params: {
          sort,
        },
      },
    );
  }

  create(answer: CreateComment, questionId: string) {
    return this.http.post<CreateAnswerResponseDto>(
      `/api/answer/${questionId}`,
      answer,
    );
  }

  update(answer: UpdateComment) {
    return this.http.patch<AnswerUpdateResponseDto>(
      `/api/answer/${answer.id}`,
      answer,
    );
  }

  delete(answerId: string) {
    return this.http.delete<DeleteAnswerResponseDto>(`/api/answer/${answerId}`);
  }

  changeVote(answerId: string, voted: VoteValues) {
    switch (voted) {
      case -1:
        return this.http.post<VoteResponseDto>(
          `/api/answer/${answerId}/vote/down`,
          {},
        );
      case 0:
        return this.http.delete<VoteResponseDto>(
          `/api/answer/${answerId}/vote`,
        );
      case 1:
        return this.http.post<VoteResponseDto>(
          `/api/answer/${answerId}/vote/up`,
          {},
        );
    }
  }

  changeAccept(answerId: string) {
    return this.http.post<AcceptAnswerDto>(
      `/api/answer/${answerId}/accept`,
      {},
    );
  }

  subscribe(answerId: string) {
    return this.http.get<Subscription>(`/api/answer/${answerId}/subscribe`);
  }

  unsubscribe(answerId: string) {
    return this.http.get<Subscription>(`/api/answer/${answerId}/unsubscribe`);
  }
}
